/* -----------------Globals--------------- */
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { Platform, type TextInput as RNTextInput } from "react-native";

/* -----------------UI--------------- */
import { Caption, TextInput } from "react-native-paper";

/* -----------------Helpers & Hooks--------------- */
import { useAppTheme } from "../../useAppTheme";

/* -----------------Types--------------- */
import type { FeedbackParams } from "./types";

/* -----------------Child components--------------- */
import Box from "../Box";

/* -----------------Libraries--------------- */
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

const MAX_FEEDBACK_LENGTH = 1000;

type FeedbackFormRef = {
	submit: () => void;
};

type FeedbackFormProps = {
	feedbackDialogParams: FeedbackParams;
	feedbackReasonList?: {
		title: string;
		description: string;
		icon: string;
		value: string;
	}[];
	onSubmit: (feedback: string, email: string) => void;
	onEmailValidationChange: (isValid: boolean) => void;
	onFeedbackValidationChange: (isValid: boolean) => void;
	onEmailTextChange: (text: string) => void;
	onFeedbackTextChange: (text: string) => void;
	feedbackLimitError: string;
	feedbackPlaceholder: string;
};

type FormValues = {
	email: string;
	feedback: string;
};

export const FeedbackForm = forwardRef<FeedbackFormRef, FeedbackFormProps>(
	(
		{
			feedbackDialogParams,
			feedbackReasonList,
			onSubmit,
			onEmailValidationChange,
			onFeedbackValidationChange,
			onEmailTextChange,
			onFeedbackTextChange,
			feedbackLimitError,
			feedbackPlaceholder,
		},
		ref,
	) => {
		const feedbackInputRef = useRef<RNTextInput>(null);
		const theme = useAppTheme();

		const isEmailRequired = !feedbackDialogParams?.email;
		const minFeedbackLength =
			feedbackReasonList && feedbackReasonList.length > 0 ? 0 : 10;

		const schema = z.object({
			email: isEmailRequired
				? z.email("Please enter a valid email address")
				: z.string(),
			feedback: z.string().min(minFeedbackLength, feedbackLimitError),
		});

		const {
			control,
			handleSubmit,
			watch,
			formState: { errors },
		} = useForm<FormValues>({
			resolver: zodResolver(schema),
			mode: "onTouched",
			defaultValues: {
				email: "",
				feedback: "",
			},
		});

		const emailValue = watch("email");
		const feedbackValue = watch("feedback");

		const submit = handleSubmit((values) => {
			onSubmit(values.feedback, values.email);
		});

		useEffect(() => {
			const timer = setTimeout(() => {
				feedbackInputRef.current?.focus();
			}, 300);
			return () => clearTimeout(timer);
		}, []);

		useImperativeHandle(ref, () => ({
			submit,
		}));

		useEffect(() => {
			if (isEmailRequired) {
				onEmailValidationChange(!errors.email && emailValue.length > 0);
			} else {
				onEmailValidationChange(true);
			}
		}, [errors.email, emailValue, isEmailRequired, onEmailValidationChange]);

		useEffect(() => {
			onFeedbackValidationChange(feedbackValue.length >= minFeedbackLength);
		}, [feedbackValue, minFeedbackLength, onFeedbackValidationChange]);

		return (
			<Box gap={24} width="100%">
				{isEmailRequired && (
					<Controller
						control={control}
						name="email"
						render={({ field: { onChange, onBlur, value } }) => (
							<Box>
								<TextInput
									label="Email"
									mode="outlined"
									placeholder="e.g., dave@gmail.com"
									onChangeText={(text) => {
										onChange(text);
										onEmailTextChange(text);
									}}
									value={value}
									onBlur={onBlur}
									autoCapitalize="none"
									keyboardType="email-address"
									onSubmitEditing={submit}
									error={!!errors.email}
								/>
							</Box>
						)}
					/>
				)}
				<Controller
					control={control}
					name="feedback"
					render={({ field: { onChange, onBlur, value } }) => (
						<Box>
							<TextInput
								ref={feedbackInputRef}
								multiline
								mode="outlined"
								autoFocus
								maxLength={MAX_FEEDBACK_LENGTH}
								error={!!errors.feedback && value.length > 0}
								style={{
									backgroundColor: theme.colors.surface,
									paddingTop: Platform.OS === "android" ? 16 : "auto",
									height: 200,
								}}
								onBlur={onBlur}
								placeholder={feedbackPlaceholder}
								value={value}
								onChangeText={(text) => {
									onChange(text);
									onFeedbackTextChange(text);
								}}
								onSubmitEditing={submit}
							/>

							{value.length >= MAX_FEEDBACK_LENGTH * 0.9 && (
								<Caption
									style={{
										textAlign: "right",
									}}
								>
									{value.length}/{MAX_FEEDBACK_LENGTH}
								</Caption>
							)}
						</Box>
					)}
				/>
			</Box>
		);
	},
);

FeedbackForm.displayName = "FeedbackForm";
